import React, { useState } from 'react';
import pipelineService, { PipelineAnalysis } from '../services/pipelineService';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

const fmt = (n: number): string => n.toLocaleString('de-DE', { maximumFractionDigits: 0 });

const Metric: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <div className="card" style={{ flex: '1 1 150px', textAlign: 'center' }}>
    <div style={{ fontSize: '22px', fontWeight: 700, color: color || 'var(--sec-fg)' }}>{value}</div>
    <div style={{ fontSize: '12px', color: 'var(--sec-fg-muted)', marginTop: '4px' }}>{label}</div>
  </div>
);

const Bar: React.FC<{ label: string; value: number; max: number; sub?: string }> = ({ label, value, max, sub }) => (
  <div style={{ marginBottom: '8px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--sec-fg)' }}>
      <span>{label}{sub ? ` · ${sub}` : ''}</span>
      <strong>{fmt(value)}</strong>
    </div>
    <div style={{ background: 'var(--sec-border)', borderRadius: '4px', height: '8px', marginTop: '2px' }}>
      <div style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, background: 'var(--sec-accent)', height: '8px', borderRadius: '4px' }} />
    </div>
  </div>
);

const SAMPLE = 'Deal,Amount,Stage,Owner,Status\nAcme,12000,Proposal,Alice,Open\nGlobex,5000,Discovery,Bob,Open\nInitech,8000,Closed Won,Alice,Won';

const PipelineAnalysisPage: React.FC = () => {
  const [csv, setCsv] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [a, setA] = useState<PipelineAnalysis | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ''));
    reader.readAsText(file);
  };

  const handleAnalyze = async () => {
    if (!csv.trim()) {
      setError('Bitte CSV-Daten einfügen oder eine Datei laden');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setA(null);
      setA(await pipelineService.analyze(csv));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler bei der Analyse');
    } finally {
      setLoading(false);
    }
  };

  const maxStage = a ? Math.max(1, ...a.byStage.map((s) => s.value)) : 1;
  const maxOwner = a ? Math.max(1, ...a.byOwner.map((o) => o.value)) : 1;

  return (
    <div className="page-container">
      <PageHeader title="Pipeline-Auswertung (Forecast)" subtitle={routeById('pipeline-analysis')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Deal-CSV einfügen oder hochladen → Pipeline-Wert, Stage-/Rep-Verteilung, Win-Rate und
        regelbasierte Insights (powered by Skill <em>pipeline-analysis</em>). Erkannte Spalten:
        Betrag/Wert, Stage/Phase, Owner/Rep, Status.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
        <div className="form-group">
          <label className="form-label">Deal-CSV</label>
          <textarea
            className="form-textarea"
            rows={8}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder="Deal,Amount,Stage,Owner,Status&#10;…"
            style={{ fontFamily: 'monospace', fontSize: '13px' }}
          />
        </div>
        <div className="btn-group" style={{ alignItems: 'center' }}>
          <button className="btn btn-success" onClick={handleAnalyze} disabled={loading}>
            {loading ? 'Analysiere…' : 'Analysieren'}
          </button>
          <input type="file" accept=".csv,text/csv" onChange={handleFile} style={{ fontSize: '13px' }} />
          <button className="btn btn-secondary btn-sm" onClick={() => setCsv(SAMPLE)} type="button">
            Beispiel einfügen
          </button>
        </div>
      </div>

      {a && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
            <Metric label="Deals" value={fmt(a.totalDeals)} />
            <Metric label="Pipeline-Wert" value={fmt(a.totalValue)} />
            <Metric label="Offen" value={fmt(a.openValue)} color="var(--sec-info)" />
            <Metric label="Gewonnen" value={fmt(a.wonValue)} color="var(--sec-success)" />
            <Metric label="Win-Rate" value={`${a.winRate}%`} color={a.winRate >= 30 ? 'var(--sec-success)' : 'var(--sec-warning)'} />
            <Metric label="Ø Deal" value={fmt(a.avgDealSize)} />
          </div>

          {a.byStage.length > 0 && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="card-title" style={{ marginBottom: '8px' }}>Wert nach Stage</div>
              {a.byStage.map((s) => (
                <Bar key={s.stage} label={s.stage} value={s.value} max={maxStage} sub={`${s.count} Deals`} />
              ))}
            </div>
          )}

          {a.byOwner.length > 0 && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="card-title" style={{ marginBottom: '8px' }}>Wert nach Owner</div>
              {a.byOwner.map((o) => (
                <Bar key={o.owner} label={o.owner} value={o.value} max={maxOwner} sub={`${o.count} Deals · ${o.won} gewonnen`} />
              ))}
            </div>
          )}

          {a.insights.length > 0 && (
            <div className="alert alert-success">
              <strong>Insights:</strong>
              <ul style={{ margin: '4px 0 0 18px' }}>
                {a.insights.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PipelineAnalysisPage;
